import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import { GoldenLayout } from 'golden-layout';
import * as ReactDOM from 'react-dom/client';
import TradingViewWidget from './TradingViewWidget';
import 'golden-layout/dist/css/goldenlayout-base.css';
import 'golden-layout/dist/css/themes/goldenlayout-dark-theme.css';

const DEFAULT_SYMBOLS = [
  { name: 'BTC/USDT', symbol: 'BINANCE:BTCUSDT' },
  { name: 'ETH/USDT', symbol: 'BINANCE:ETHUSDT' },
  { name: 'LINK/USDT', symbol: 'BINANCE:LINKUSDT' },
  { name: 'ADA/USDT', symbol: 'BINANCE:ADAUSDT' },
  { name: 'BTC.D', symbol: 'CRYPTOCAP:BTC.D' },
  { name: 'AAPL', symbol: 'NASDAQ:AAPL' },
  { name: 'NVDA', symbol: 'NASDAQ:NVDA' },
  { name: 'S&P 500', symbol: 'FOREXCOM:SPX500' }
];

const GoldenLayoutComponent = React.forwardRef((props, ref) => {
  const containerRef = useRef(null);
  const layoutRef = useRef(null);

  const addChart = (symbol) => {
    if (!layoutRef.current) return;

    // Format the symbol to include exchange if not specified
    let formattedSymbol = symbol.toUpperCase();
    if (!formattedSymbol.includes(':')) {
      // Default to BINANCE for USDT pairs, NASDAQ for others
      formattedSymbol = formattedSymbol.includes('USDT') ? 
        `BINANCE:${formattedSymbol}` : 
        `NASDAQ:${formattedSymbol}`;
    }

    const config = {
      type: 'component',
      componentName: 'tradingView',
      componentState: { symbol: formattedSymbol },
      title: formattedSymbol
    };

    // Use the built-in addComponent method
    layoutRef.current.addComponent(
      config.componentName,
      config.componentState,
      config.title,
      { 
        x: 0,
        y: 1,
        w: 3,
        h: 2
      }
    );
  };

  // Update theme when it changes
  useEffect(() => {
    if (layoutRef.current) {
      const container = layoutRef.current.container;
      if (container) {
        container.classList.toggle('dark-theme', props.isDarkMode);
        container.classList.toggle('light-theme', !props.isDarkMode);
      }
    }
  }, [props.isDarkMode]);

  useEffect(() => {
    if (!containerRef.current) return;

    const layout = new GoldenLayout(containerRef.current);
    layoutRef.current = layout;

    // Register components
    const registerComponent = () => {
      if (!layoutRef.current) return;
      
      layoutRef.current.registerComponent('tradingView', (container, componentState) => {
        const root = ReactDOM.createRoot(container.element);
        root.render(
          <TradingViewWidget 
            symbol={componentState.symbol} 
            theme={componentState.theme}
          />
        );
        return () => root.unmount();
      });
    };

    // Create initial layout
    const initialLayout = {
      settings: {
        showPopoutIcon: false,
        showMaximiseIcon: true,
        showCloseIcon: true
      },
      dimensions: {
        borderWidth: 5,
        minItemHeight: 100,
        minItemWidth: 100,
        headerHeight: 20,
        dragProxyWidth: 300,
        dragProxyHeight: 200
      },
      content: [{
        type: 'column',
        content: [
          {
            type: 'row',
            height: 50,
            content: DEFAULT_SYMBOLS.slice(0, 4).map(symbol => ({
              type: 'component',
              componentName: 'tradingView',
              componentState: { 
                symbol: symbol.symbol,
                theme: props.isDarkMode ? 'light' : 'dark'
              },
              title: symbol.name,
              width: 25
            }))
          },
          {
            type: 'row',
            height: 50,
            content: DEFAULT_SYMBOLS.slice(4).map(symbol => ({
              type: 'component',
              componentName: 'tradingView',
              componentState: { 
                symbol: symbol.symbol,
                theme: props.isDarkMode ? 'light' : 'dark'
              },
              title: symbol.name,
              width: 25
            }))
          }
        ]
      }]
    };

    registerComponent();
    layout.loadLayout(initialLayout);

    // Handle window resize
    const resizeListener = () => layout.updateSize();
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
      layout.destroy();
    };
  }, []);

  // Expose addChart method via ref
  useImperativeHandle(ref, () => ({
    addChart: (symbol) => {
      if (!layoutRef.current) return;

      // Format the symbol to include exchange if not specified
      let formattedSymbol = symbol.toUpperCase();
      if (!formattedSymbol.includes(':')) {
        formattedSymbol = formattedSymbol.includes('USDT') ? 
          `BINANCE:${formattedSymbol}` : 
          `NASDAQ:${formattedSymbol}`;
      }

      // Create new component config
      const config = {
        type: 'component',
        componentName: 'tradingView',
        componentState: { 
          symbol: formattedSymbol,
          theme: props.isDarkMode ? 'light' : 'dark'
        },
        title: formattedSymbol
      };

      // Add the component
      layoutRef.current.addComponent(
        config.componentName,
        config.componentState,
        config.title
      );
    }
  }));

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: 'calc(100vh - 4rem)', // Subtract header height
        position: 'relative',
        overflow: 'hidden'
      }} 
    />
  );
});

export default React.memo(GoldenLayoutComponent);
